import { defineStore } from 'pinia';
import { cloneDeep, get, parseInt, round, sortBy, pick } from 'lodash';
import uniqid from 'uniqid';
import { fromUnixTime, isFuture, isPast } from 'date-fns';
import { appFromUnixTime } from '@/utils/timezone';

import MemberApi from '@/extensions/restrictedArea/store/api/member';
import PurchaseApi from '@/extensions/shop/pointOfSale/store/api/purchase';
import PurchasedItemApi from '@/extensions/shop/store/api/purchasedItem';
import ArticleApi from '@/extensions/shop/store/api/article';
import ProductApi from '@/extensions/shop/store/api/product';
import CartApi from '@/extensions/shop/pointOfSale/store/api/cart';
import CouponApi from '@/extensions/shop/pointOfSale/store/api/coupon';

import pinia from '@/pinia';
import { useSetupStore, usePosSetupStore } from '@/stores';

/**
 * Generate item object for cart
 * @param {Object} shop - Shop configuration with tax settings
 * @param {Object} article - Article to add to cart
 * @param {Number|Array} qty - Quantity or array of quantities per stock
 * @param {Array} selectedPropertyPivots - Selected property pivots
 * @param {Array} stocks - Available stocks
 * @returns {Object} Cart item object
 */
const generateItemObject = (shop, article, qty, selectedPropertyPivots, stocks) => {
    selectedPropertyPivots = selectedPropertyPivots || [];

    let tax = get(article.price, 'tax.value');
    let taxRate = get(article.price, 'tax_rate');

    if (article.salePrice) {
        tax = get(article.salePrice, 'tax.value');
        taxRate = get(article.salePrice, 'tax_rate');
    }

    if (tax == null && taxRate) {
        switch (taxRate) {
            case 'regular':
                tax = get(shop, 'activeRegularTax.value') || get(shop, 'defaultTax.value');
                break;

            case 'reduced':
                tax = get(shop, 'activeReducedTax.value') || get(shop, 'defaultReducedTax.value');
                break;

            case 'none':
                tax = 0;
                break;
        }
    }

    if (tax == null) {
        tax = get(shop, 'defaultTax.value');
    }

    let properties = [];
    let propertyOrder = {};
    article.product.propertyPivots.forEach((el, i) => {
        propertyOrder[el.property.id] = i + 1;
        if (!el.valueOptions.length || el.property.article_required) return;

        let selected = false;
        let valueOption = el.valueOptions[0];
        if (selectedPropertyPivots.length) {
            selected = selectedPropertyPivots.find(pivot => get(pivot, 'property.id') === el.property.id);
            if (selected) valueOption = selected.valueOption;
        }

        const surcharge = get(valueOption, 'pivot.price.value') || get(valueOption, 'price.value');
        if (selected || surcharge) {
            const property = {
                id: el.property.id,
                key: el.property.key,
                name: el.property.name,
                unit: el.property.unit,
                article_required: el.property.article_required,
                selectable: el.property.article_required || el.valueOptions.length > 1,
                valueOption: pick(valueOption, ['id', 'text', 'description', 'addition', 'number', 'date', 'color']),
                surcharge,
                propType: pick(el.property.propType, ['id', 'key', 'name', 'value_field', 'value_field_alt']),
                position: get(propertyOrder, el.property.id, Object.keys(propertyOrder).length + 1),
            };

            properties.push(property);
        }
    });

    article.propertyPivots.forEach(el => {
        const property = {
            id: el.property.id,
            key: el.property.key,
            name: el.property.name,
            unit: el.property.unit,
            article_required: el.property.article_required,
            selectable: true,
            valueOption: pick(el.valueOption, ['id', 'text', 'description', 'addition', 'number', 'date', 'color']),
            propType: pick(el.property.propType, ['id', 'key', 'name', 'value_field', 'value_field_alt']),
            position: get(propertyOrder, el.property.id, Object.keys(propertyOrder).length + 1),
        };

        properties.push(property);
    });

    let surcharge = 0;
    properties.forEach(property => surcharge += property.surcharge || 0);

    let item = {
        id: uniqid('new:'),
        product_nr: article.product.nr,
        sku: article.sku,
        name: article.name || article.product.name,
        description: article.description || article.product.description,
        price: get(article.price, 'value') + surcharge,
        sale_price: get(article.salePrice, 'value') ? (article.salePrice.value + surcharge) : undefined,
        tax,
        manage_stock: article.product.manage_stock,
        properties: sortBy(properties, 'position'),
        article,
    };

    if (article.product.manage_stock && typeof qty === 'object' && qty !== null && Array.isArray(qty)) {
        item.quantities = qty;
    } else {
        item.qty = qty;
    }

    return item;
};

export const usePosCartStore = defineStore('posCart', {
    state: () => ({
        id: undefined,
        items: [],
        coupons: [],
        purchaser: {
            id: uniqid('new:'),
            name: 'Guest',
            customer: undefined
        }
    }),

    getters: {
        applicableCustomerCoupons: (state) => {
            let coupons = get(state.purchaser, 'customer.coupons') || [];
            if (coupons.length) {
                const canApplyCoupon = coupon => {
                    let canApply = true;
                    if (['customer', 'customer-group', 'all', 'anyone'].includes(coupon.applicable_for) === false) canApply = false;
                    else if (coupon.max_uses_per_customer) canApply = get(coupon, 'pivot.uses', 0) < coupon.max_uses_per_customer;
                    return canApply;
                };

                coupons = sortBy(coupons, [
                    (coupon) => coupon.position != null ? parseInt(coupon.position) : 0,
                    (coupon) => parseInt(coupon.id),
                ]).filter(coupon => {
                    let applicable = true;

                    if (coupon.code !== null) applicable = false;

                    if (applicable && coupon.status !== 'enabled')
                        applicable = false;

                    if (applicable && coupon.max_uses !== null && coupon.times_used >= coupon.max_uses)
                        applicable = false;

                    if (applicable && coupon.max_uses_per_customer !== null && canApplyCoupon(coupon) === false)
                        applicable = false;

                    if (applicable && coupon.start_at && isFuture(appFromUnixTime(coupon.start_at)))
                        applicable = false;

                    if (applicable && coupon.end_at && isPast(appFromUnixTime(coupon.end_at)))
                        applicable = false;

                    return applicable;
                });
            }

            return coupons;
        },

        qtySum: (state) => {
            let qtySum = 0;
            for (const item of state.items) {
                if (item.manage_stock && item.quantities) {
                    for (const el of item.quantities) {
                        qtySum += el.qty;
                    }
                }
                else {
                    qtySum += item.qty;
                }
            }
            return qtySum;
        },

        subTotal: (state) => {
            let total = 0;
            for (const item of state.items) {
                if (item.manage_stock && item.quantities) {
                    for (const el of item.quantities) {
                        total += (item.sale_price || item.price || 0) * el.qty;
                    }
                }
                else {
                    total += (item.sale_price || item.price || 0) * item.qty;
                }
            }
            return total;
        },

        discount() {
            let total = 0;

            if (this.coupons.length) {
                // NOTE: percentage base
                let remainingSubTotal = this.subTotal;
                this.coupons.forEach(coupon => {
                    if (coupon.disabled) return;
                    switch (coupon.scope) {
                        case 'product':
                            // TODO
                            break;

                        case 'article':
                            // TODO
                            break;

                        default:
                        case 'cart':
                        case 'purchase':
                            // NOTE: apply coupon either fixed or the percentage to the whole shopping cart
                            if (coupon.type === 'percentage') {
                                const percentage = parseFloat(coupon.percentage);
                                const amount = (remainingSubTotal / 100) * percentage;

                                remainingSubTotal -= amount;
                                total += amount;
                            } else if (coupon.type === 'fixed') {
                                total += coupon.amount;
                            }
                            break;
                    }
                });
            }

            total = round(total, 2);
            return Math.min(total, this.subTotal);
        },

        total() {
            return this.subTotal - this.discount;
        }
    },

    actions: {
        // Mutation equivalents (converted to actions)
        setId(id) {
            this.id = id;
        },

        addItem({ article, selectedPropertyPivots, id, qty, salePrice }) {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const shop = cloneDeep(setupStore.activeCms.shop);

            const increaseBy = qty || 1;

            const activeRegularTax = posSetupStore.activeRegularTax;
            if (activeRegularTax) shop.activeRegularTax = activeRegularTax;

            const activeReducedTax = posSetupStore.activeReducedTax;
            if (activeReducedTax) shop.activeReducedTax = activeReducedTax;

            let i;
            if (selectedPropertyPivots && selectedPropertyPivots.length) {
                const selectedValueOptionIDs = selectedPropertyPivots.map(el => el.valueOption.id);

                i = this.items.findIndex(el => {
                    const diff = el.properties.filter(property => !selectedValueOptionIDs.some(id => id == property.valueOption.id));
                    return el.article.id == article.id && !diff.length;
                });
            } else {
                i = this.items.findIndex(el => el.article.id == article.id);
            }

            const stocks = (get(posSetupStore, 'activePointOfSale.stocks') || [])
                .sort((a, b) => b.priority_pos - a.priority_pos);

            // item already in cart list
            if (i >= 0) {
                // managed stock
                if (article.product.manage_stock) {
                    let stockIdWithQty = null;

                    // try to find stock with available qty
                    article.stocks
                        .sort((a, b) => b.priority_pos - a.priority_pos)
                        .forEach((articleStock) => {
                            if (stocks.find(stock => stock.id == articleStock.id) && articleStock.pivot.qty > 0 && stockIdWithQty == null) {
                                stockIdWithQty = articleStock.id;
                            }
                        });

                    // no stock with available qty, choose first
                    if (stockIdWithQty == null) stockIdWithQty = stocks[0].id;

                    // increase qty
                    const quantityItem = this.items[i].quantities.find(quantity => quantity.stock.id == stockIdWithQty);
                    if (quantityItem) {
                        quantityItem.qty += increaseBy;
                    }
                }
                // unmanaged stock -> simply increase qty
                else {
                    this.items[i].qty = this.items[i].qty + increaseBy;
                }
            }
            // new item, not in cart list yet
            else {
                let itemQty;

                // managed stock
                if (article.product.manage_stock) {
                    let stockIdWithQty = null;

                    // try to find stock with available qty
                    article.stocks
                        .sort((a, b) => b.priority_pos - a.priority_pos)
                        .forEach((articleStock) => {
                            if (stocks.find(stock => stock.id == articleStock.id) && articleStock.pivot.qty >= increaseBy && stockIdWithQty == null) {
                                stockIdWithQty = articleStock.id;
                            }
                        });

                    let quantities = [];
                    stocks.forEach((stock, stockIndex) => {
                        let stockQty = 0;

                        // no stock with available qty found, set first stock qty to 1, others to 0
                        if (stockIdWithQty === null) {
                            if (stockIndex === 0) stockQty = increaseBy;
                            else stockQty = 0;
                        }
                        // stock with available qty found, set it to 1, others to 0
                        else {
                            if (stock.id === stockIdWithQty) stockQty = increaseBy;
                            else stockQty = 0;
                        }

                        const quantity = {
                            stock: { id: parseInt(stock.id) },
                            qty: stockQty,
                        };

                        quantities.push(quantity);
                    });

                    itemQty = quantities;
                } else {
                    itemQty = increaseBy;
                }

                const item = generateItemObject(shop, article, itemQty, selectedPropertyPivots, stocks);

                if (id) item.id = id;
                if (salePrice) item.sale_price = salePrice;

                this.items.push(item);
            }
        },

        deleteItem(item) {
            const i = this.items.findIndex(el => el.id === item.id);
            if (i >= 0) {
                this.items.splice(i, 1);
            }

            // if the cart is empty delete it
            if (this.items.length === 0) {
                this.id = undefined;
            }
        },

        updateItem(item) {
            const i = this.items.findIndex(el => el.id === item.id);
            if (i >= 0) {
                this.items.splice(i, 1, item);
            }
        },

        updateArticle(article) {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const shop = cloneDeep(setupStore.activeCms.shop);

            const activeRegularTax = posSetupStore.activeRegularTax;
            if (activeRegularTax) shop.activeRegularTax = activeRegularTax;

            const activeReducedTax = posSetupStore.activeReducedTax;
            if (activeReducedTax) shop.activeReducedTax = activeReducedTax;

            const i = this.items.findIndex(el => el.article.id === article.id);

            if (i >= 0) {
                const id = this.items[i].id;
                const sale_price = this.items[i].sale_price;
                const properties = this.items[i].properties;

                let qty = this.items[i].qty;
                if (article.product.manage_stock) qty = this.items[i].quantities;

                const stocks = get(posSetupStore, 'activePointOfSale.stocks') || [];

                let item = generateItemObject(shop, article, qty, [], stocks);
                if (sale_price != null) item.sale_price = sale_price;
                if (properties) {
                    item.properties = properties;

                    let surcharge = 0;
                    properties.forEach(property => surcharge += property.surcharge || 0);
                    item.price += surcharge;
                }
                item.id = id;

                this.items.splice(i, 1, item);
            }
        },

        updateArticleStocks(article) {
            const i = this.items.findIndex(el => el.article.id === article.id);
            if (i >= 0) {
                this.items[i].article.stocks = article.stocks;
            }
        },

        updateItemQty({ item, qty }) {
            const i = this.items.findIndex(el => el.id === item.id);
            if (i >= 0) {
                this.items[i].qty = qty;
            }
        },

        updateItemQuantities({ item, qty, stock }) {
            const i = this.items.findIndex(el => el.id === item.id);
            if (i >= 0) {
                const j = this.items[i].quantities.findIndex(el => parseInt(el.stock.id) == stock.id);
                if (j >= 0) {
                    this.items[i].quantities[j].qty = qty;
                }
            }
        },

        setCart(cart) {
            if (cart) {
                this.id = get(cart, 'id');
                this.items = cart.items || [];
                this.coupons = cart.coupons || [];
                this.purchaser = cart.purchaser;
            } else {
                this.id = undefined;
                this.items = [];
                this.coupons = [];
                this.purchaser = {
                    id: uniqid('new:'),
                    name: 'Guest',
                    customer: undefined
                };
            }
        },

        setPurchaser(purchaser) {
            if (purchaser) {
                this.purchaser = purchaser;
            } else {
                this.purchaser = {
                    id: uniqid('new:'),
                    name: 'Guest',
                    customer: undefined
                };
                this.coupons = [];
            }
        },

        setCoupons(coupons) {
            this.coupons = coupons || [];
        },

        addCoupon(coupon) {
            let coupons = cloneDeep(this.coupons);
            coupons = sortBy(coupons, [
                (c) => c.position != null ? parseInt(c.position) : 0,
                (c) => parseInt(c.id),
            ]);

            let i = 0;
            // check if the requested coupon is already in state
            if (coupons.length > 0) i = coupons.findIndex(el => el.id === coupon.id);
            if (i < 0) i = coupons.length;
            coupons.splice(i, 1, coupon);

            if (coupons[i].disabled) {
                delete coupons[i].disabled;
            }

            let skip = false;
            coupons = sortBy(coupons, [
                (c) => c.position != null ? parseInt(c.position) : 0,
                (c) => parseInt(c.id),
            ]).filter(el => {
                if (!skip) {
                    if (el.discard_the_following_coupons) skip = true;
                    return el;
                }
            });
            this.coupons = coupons;
        },

        removeCoupon(coupon) {
            const coupons = this.coupons || [];
            const i = coupons.findIndex(el => el.id === coupon.id);
            if (i >= 0) {
                this.coupons.splice(i, 1);
            }
        },

        disableCoupon(coupon) {
            const coupons = this.coupons || [];
            const i = coupons.findIndex(el => el.id === coupon.id);
            if (i >= 0) {
                this.coupons[i].disabled = true;
            }
        },

        // Action equivalents
        initializeCustomerCoupons() {
            const coupons = this.applicableCustomerCoupons || [];
            coupons.forEach(coupon => this.addCoupon(coupon));
        },

        removeCouponFromCart(coupon) {
            // Check if this is a customer auto-applied coupon
            // (applicable for active customer AND code === null)
            const applicableCustomerCoupons = this.applicableCustomerCoupons || [];
            const isCustomerAutoApplied = coupon.code === null &&
                applicableCustomerCoupons.some(el => el.id === coupon.id);

            if (isCustomerAutoApplied) {
                // Disable customer auto-applied coupons (keep in list but inactive)
                this.disableCoupon(coupon);
            } else {
                // Remove manually entered coupons completely
                this.removeCoupon(coupon);
            }
        },

        findArticle(payload) {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const cms = setupStore.activeCms;
            const features = get(cms, 'config.template.features') || [];
            const customerPrices = !!features.find(el => el === 'pos:customer-prices');

            if (!payload.shop) payload.shop = parseInt(cms.shop.id);

            let mimicCustomer;
            if (customerPrices) mimicCustomer = this.purchaser.customer ? { id: this.purchaser.customer.id } : null;

            const customerGroup = get(posSetupStore, 'activePointOfSale.customerGroup')
                || get(setupStore, 'activeCms.shop.defaultCustomerGroup');
            const address = get(posSetupStore, 'activePointOfSale.address');
            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                };
            }

            if (mimicCustomer) payload.mimicCustomer = mimicCustomer;

            return ArticleApi.findArticle(payload, 'cache-first', { retries: 1 });
        },

        fetchArticles(payload) {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const cms = setupStore.activeCms;
            const features = get(cms, 'config.template.features') || [];
            const customerPrices = !!features.find(el => el === 'pos:customer-prices');

            if (!payload.shop) payload.shop = parseInt(cms.shop.id);

            let mimicCustomer;
            if (customerPrices) mimicCustomer = this.purchaser.customer ? { id: this.purchaser.customer.id } : null;

            const customerGroup = get(posSetupStore, 'activePointOfSale.customerGroup')
                || get(setupStore, 'activeCms.shop.defaultCustomerGroup');
            const address = get(posSetupStore, 'activePointOfSale.address');
            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                };
            }

            if (mimicCustomer) payload.mimicCustomer = mimicCustomer;
            return ArticleApi.fetchArticles(payload);
        },

        fetchProductsPaginated(payload) {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const cms = setupStore.activeCms;
            const features = get(cms, 'config.template.features') || [];
            const customerPrices = !!features.find(el => el === 'pos:customer-prices');

            let mimicCustomer;
            if (customerPrices) mimicCustomer = this.purchaser.customer ? { id: this.purchaser.customer.id } : null;

            const customerGroup = get(posSetupStore, 'activePointOfSale.customerGroup')
                || get(setupStore, 'activeCms.shop.defaultCustomerGroup');
            const address = get(posSetupStore, 'activePointOfSale.address');
            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                };
            }

            return ProductApi.fetchProductsPaginated({ ...payload, mimicCustomer, includeConfig: false }, false, true);
        },

        fetchPurchasedItemsPaginated(payload) {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const cms = setupStore.activeCms;
            const features = get(cms, 'config.template.features') || [];
            const customerPrices = !!features.find(el => el === 'pos:customer-prices');

            let mimicCustomer;
            if (customerPrices) mimicCustomer = this.purchaser.customer ? { id: this.purchaser.customer.id } : null;

            const customerGroup = get(posSetupStore, 'activePointOfSale.customerGroup')
                || get(setupStore, 'activeCms.shop.defaultCustomerGroup');
            const address = get(posSetupStore, 'activePointOfSale.address');
            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                };
            }

            return PurchasedItemApi.fetchPurchasedItemsPaginated({ ...payload, mimicCustomer });
        },

        save() {
            // Store only coupon codes instead of full coupon objects
            // This ensures fresh coupon data is fetched on page reload
            const cartToSave = {
                id: this.id,
                items: this.items,
                purchaser: this.purchaser,
                couponCodes: this.coupons.filter(c => !c.disabled).map(c => c.code),
                coupons: undefined, // Don't store full coupon objects
            };
            CartApi.saveCart(cartToSave);
        },

        clear() {
            this.setCart();
            CartApi.removeCart();
        },

        load() {
            const setupStore = useSetupStore(pinia);
            const posSetupStore = usePosSetupStore(pinia);
            const cms = setupStore.activeCms;
            const features = get(cms, 'config.template.features') || [];
            const customerPrices = !!features.find(el => el === 'pos:customer-prices');

            const savedCart = CartApi.getCart({
                id: this.id,
                items: this.items,
                coupons: this.coupons,
                purchaser: this.purchaser
            });
            const couponCodes = (savedCart && savedCart.couponCodes) || [];
            this.setCart(savedCart);

            const shop = parseInt(cms.shop.id);
            const pointOfSale = parseInt(get(posSetupStore, 'activePointOfSale.id'));
            const ids = this.items.map(el => parseInt(el.article.id));

            const customerID = get(this.purchaser, 'customer.id');

            posSetupStore.fetchPurchasesOnHold();

            let mimicCustomer;
            if (customerPrices) mimicCustomer = this.purchaser.customer ? { id: this.purchaser.customer.id } : null;

            const customerGroup = get(posSetupStore, 'activePointOfSale.customerGroup')
                || get(setupStore, 'activeCms.shop.defaultCustomerGroup');
            const address = get(posSetupStore, 'activePointOfSale.address');
            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                };
            }

            let actions = [];
            if (customerID) {
                const fetchMember = MemberApi.fetchMember({ customer: customerID, cms: setupStore.activeCms.id }).then((member) => {
                    if (member && member.customer) {
                        const purchaser = {
                            id: this.purchaser.id,
                            name: member.name,
                            customer: cloneDeep(member.customer)
                        };
                        this.setPurchaser(purchaser);
                        this.initializeCustomerCoupons();
                    } else {
                        this.setPurchaser();
                        posSetupStore.fetchPurchasesOnHold();
                    }
                });

                actions.push(fetchMember);
            }

            const fetchArticles = ArticleApi.fetchArticles({ ids, shop, mimicCustomer })
                .then(articles => {
                    const articleIds = articles.map(el => el.id);
                    this.items.forEach(item => {
                        if (articleIds.includes(item.article.id) === false) this.deleteItem(item);
                    });
                    articles.forEach(article => this.updateArticle(article));
                    this.save();
                });

            actions.push(fetchArticles);

            // Re-fetch coupons from stored codes to get fresh data
            if (couponCodes.length > 0 && shop && pointOfSale) {
                const fetchCoupons = Promise.all(
                    couponCodes.map(code =>
                        CouponApi.apply(code, shop, pointOfSale, customerID)
                            .then(coupon => {
                                if (coupon) this.addCoupon(coupon);
                            })
                            .catch(() => {
                                // Coupon may no longer be valid, silently ignore
                            })
                    )
                ).then(() => this.save());

                actions.push(fetchCoupons);
            }

            return Promise.all(actions);
        },

        sell({ purchase, split, returnRemainingCouponAmountAsCash }) {
            const posSetupStore = usePosSetupStore(pinia);
            return PurchaseApi.sell(purchase, posSetupStore.activePointOfSale, split, returnRemainingCouponAmountAsCash);
        },

        sellLater(purchase) {
            const posSetupStore = usePosSetupStore(pinia);
            return PurchaseApi.sellLater(purchase, posSetupStore.activePointOfSale);
        },

        requestArticles(purchase) {
            const posSetupStore = usePosSetupStore(pinia);
            return PurchaseApi.requestArticles(purchase, posSetupStore.activePointOfSale);
        }
    }
});
